import React, { useState } from 'react';
import { View, TextInput, KeyboardTypeOptions, NativeSyntheticEvent, TextInputKeyPressEventData, ViewStyle, StyleProp, TextStyle, TextInputProps } from 'react-native';
import { translate } from '../services/translate';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { CustomTooltip } from './CustomTooltip';
import { Hoverable } from 'react-native-web-hover';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

type ModeType = 'TEXT' | 'NUMBER';

type FormInputProps = {
    toUppercase?: boolean;
    label?: string;
    placeholder?: string;
    errorMessage?: string;
    invalid?: (value: string) => boolean;
    disabled?: boolean;
    secureTextEntry?: boolean;
    keyboardType?: KeyboardTypeOptions;
    onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
    maxLength?: number;
    viewStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
    inputStyle?: StyleProp<TextStyle>;
    multiline?: boolean;
    numberOfLines?: number;
    labelFontSize?: number;
    autoComplete?: TextInputProps['autoComplete'];
    required?: boolean;
    hintText?: string | React.ReactNode | React.ReactNode;
    hintTextConfig?: {
        tooltipStyle?: React.CSSProperties;
        tooltipDirection?: 'right' | 'left' | 'top';
    };
    semiBoldLabel?: boolean;
    maxCharacters?: number;
    minValue?: number;
    maxValue?: number;
    mode?: ModeType;
    mask?: (val: string) => string;
    onContentSizeChange?: (val: { nativeEvent: { contentSize: { width: number; height: number; }; }; }) => void;
    prefix?: string;
} & ({
    form?: undefined;
    fieldName?: undefined;
    customValidations?: undefined;
    pattern?: undefined;
    value: string;
    onChangeText: (value: string) => void;
} | {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    value?: undefined;
    onChangeText?: undefined;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
});

export default function FormInput({
    toUppercase,
    label,
    labelFontSize,
    placeholder,
    errorMessage,
    value,
    onChangeText,
    invalid,
    disabled,
    secureTextEntry,
    maxLength = 255,
    keyboardType,
    onKeyPress,
    viewStyle,
    labelStyle,
    inputStyle,
    multiline,
    numberOfLines,
    autoComplete,
    required,
    hintText,
    hintTextConfig,
    maxCharacters,
    minValue,
    maxValue,
    mode = 'TEXT',
    form,
    fieldName,
    customValidations,
    pattern,
    mask,
    onContentSizeChange,
    prefix,
    semiBoldLabel = false
}: FormInputProps) {
    const { styles, theme } = useStyles(stylesheet);
    const [isDirty, setIsDirty] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);
    const [hintHovered, setHintHovered] = useState(false);

    disabled = !!disabled;

    if (form && fieldName) {

        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        rules.required = required ? translate('required_field') : false;

        if (pattern) {
            rules.pattern = pattern;
        }

        if (maxLength) {
            rules.maxLength = maxLength;
        }

        if (customValidations) {
            rules.validate = { ...customValidations };
        }

        if (mode == 'NUMBER' && minValue) {
            rules.min = minValue;
        }

        if (mode == 'NUMBER' && maxValue) {
            rules.max = maxValue;
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[hintHovered && { zIndex: 9999 }, viewStyle]}>
                    <View style={[{ flexDirection: 'row', columnGap: 5 }, hintText ? { zIndex: 9999 } : {}]}>
                        {label && <MyAppText style={labelStyle ? labelStyle : styles.label(labelFontSize ?? 16, semiBoldLabel)}>{label}</MyAppText>}
                        {required && label ?
                            <MyAppText style={styles.filterTextRequired(semiBoldLabel)}>(*)</MyAppText>
                            : null}
                        {hintText
                            ? <Hoverable>
                                {({ hovered }) => {
                                    setHintHovered(hovered);
                                    return <CustomTooltip
                                        content={hintText}
                                        tooltipStyle={hintTextConfig?.tooltipStyle}
                                        tooltipDirection={hintTextConfig?.tooltipDirection}
                                    />;
                                }}
                            </Hoverable>
                            : null
                        }
                    </View>
                    <View style={prefix ? styles.inputRow : undefined}>
                        {prefix ? (
                            <View style={[styles.prefixBox, fieldState.error ? styles.invalid : null]}>
                                <MyAppText style={styles.prefixText}>{prefix}</MyAppText>
                            </View>
                        ) : null}
                        <TextInput
                            onBlur={field.onBlur}
                            autoComplete={autoComplete}
                            editable={!disabled}
                            placeholder={placeholder ? placeholder : label}
                            style={[styles.input, prefix ? styles.inputWithPrefix : null, inputStyle, {
                                color: !field.value ? theme.colors.placeholder : theme.colors.textColor
                            }, disabled ? styles.disabled : (isInvalid || fieldState.error ? styles.invalid : null), !multiline ? { height: 40 } : {}]}
                            value={mask && field.value ? mask(field.value) : field.value}
                            onChangeText={(value) => {
                                if (toUppercase) {
                                    value = value.toUpperCase();
                                }
                                if (mode === 'NUMBER') {
                                    value = value.replace(/[^0-9]/g, '');
                                }
                                field.onChange(value);
                            }}
                            secureTextEntry={secureTextEntry}
                            keyboardType={keyboardType}
                            onKeyPress={onKeyPress}
                            maxLength={maxLength}
                            multiline={multiline}
                            numberOfLines={numberOfLines}
                            onContentSizeChange={onContentSizeChange}
                        />
                    </View>
                    {maxCharacters ? <MyAppText style={styles.maxCharacters}>{translate('characters')}: {field.value.length}/{maxCharacters}</MyAppText> : null}
                    {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;
    } else if (value !== undefined && onChangeText !== undefined) {
        return (
            <View style={[hintHovered && { zIndex: 9999 }, viewStyle]}>
                <View style={[{ flexDirection: 'row', columnGap: 5 }, hintText ? { zIndex: 9999 } : {}]}>
                    {label && <MyAppText style={labelStyle ? labelStyle : styles.label(labelFontSize ?? 16, semiBoldLabel)}>{label}</MyAppText>}
                    {required ?
                        <MyAppText style={styles.filterTextRequired(semiBoldLabel)}>(*)</MyAppText>
                        : null}
                    {hintText
                        ? <Hoverable>
                            {({ hovered }) => {
                                setHintHovered(hovered);
                                return <CustomTooltip
                                    content={hintText}
                                    tooltipStyle={hintTextConfig?.tooltipStyle}
                                    tooltipDirection={hintTextConfig?.tooltipDirection}
                                />;
                            }}
                        </Hoverable>
                        : null
                    }
                </View>
                <TextInput
                    autoComplete={autoComplete}
                    editable={!disabled}
                    placeholder={placeholder ? placeholder : label}
                    style={[styles.input, inputStyle, {
                        color: value == '' ? theme.colors.placeholder : theme.colors.textColor
                    }, disabled ? styles.disabled : (isInvalid ? styles.invalid : null), !multiline ? { height: 40 } : {}]}
                    value={mask && value ? mask(value) : value}
                    onBlur={() => {
                        if (invalid) {
                            setIsInvalid(isDirty && invalid(value));
                        }
                    }}
                    onChangeText={(value) => {
                        if (invalid && !invalid(value)) {
                            setIsInvalid(false);
                        }
                        setIsDirty(true);

                        if (mode === 'NUMBER') {
                            value = value.replace(/[^0-9]/g, '');
                            if (minValue !== undefined && Number(value) < minValue) {
                                setIsInvalid(true);
                            } else if (maxValue !== undefined && Number(value) > maxValue) {
                                setIsInvalid(true);
                            } else {
                                setIsInvalid(false);
                            }
                        }

                        if (maxCharacters) {
                            value = value.slice(0, maxCharacters);
                        }

                        if (toUppercase) {
                            value = value.toUpperCase();
                        }

                        onChangeText(value);
                    }}
                    secureTextEntry={secureTextEntry}
                    keyboardType={keyboardType}
                    onKeyPress={onKeyPress}
                    maxLength={maxLength}
                    multiline={multiline}
                    numberOfLines={numberOfLines}
                    onContentSizeChange={onContentSizeChange}
                />
                {maxCharacters ? <MyAppText style={styles.maxCharacters}>{translate('characters')}: {value.length}/{maxCharacters}</MyAppText> : null}
                {isInvalid ?
                    <MyAppText style={styles.error}>
                        {
                            minValue !== undefined && Number(value) < minValue ? translate('value_bellow_min_accept', { minValue }) :
                                maxValue !== undefined && Number(value) > maxValue ? translate('value_over_max_accept', { maxValue }) :
                                    errorMessage ? errorMessage : translate('required_field')
                        }
                    </MyAppText>
                    : null}
            </View>
        );
    }

}

const stylesheet = createStyleSheet(theme => ({
    label: (fontSize: number, semiBoldLabel: boolean) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
        fontWeight: semiBoldLabel ? '600' : '400'
    }),
    filterTextRequired: (semiBoldLabel?: boolean) => ({
        color: theme.colors.errors['300'],
        fontWeight: semiBoldLabel ? '600' : '400'
    }),
    input: {
        fontSize: 16,
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        padding: 10,
        borderRadius: 4,
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
    invalid: {
        borderColor: theme.colors.errors['500'],
    },
    disabled: {
        backgroundColor: theme.colors.field.disabledBackground,
    },
    maxCharacters: {
        alignSelf: 'flex-end',
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.neutral['400']
    },
    inputRow: {
        flexDirection: 'row',
        alignItems: 'stretch',
    },
    prefixBox: {
        justifyContent: 'center',
        paddingHorizontal: 12,
        height: 40,
        backgroundColor: theme.colors.field.disabledBackground,
        borderColor: theme.colors.field.border,
        borderWidth: 1,
        borderRightWidth: 0,
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
        flexShrink: 0,
    },
    prefixText: {
        fontSize: 16,
        color: theme.colors.neutral['400'],
    },
    inputWithPrefix: {
        flex: 1,
        minWidth: 0,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
    },
}));
